/**
 * Block settings per device.
 *
 * Desktop keeps the value on the block. Tablet and phone store only what
 * differs, on `block.devices`, beside a gallery's own columns and gap.
 * A device with no value of its own uses desktop's.
 *
 *   block.devices = { tablet?: { marginTop?, height?, … }, phone?: { … } }
 *
 * The public page and the editor preview pick a device with the same widths
 * as the columns block (and `html[data-header-bp]` when the editor forces one).
 */

import { captionVars, cleanCaption } from './caption.js';
import { columnColors, colorVars } from './colors.js';
import { contentWidthCss } from './content-width.js';
import { cssBackdropFilter, cssFilter } from './filter.js';
import { cssSpace } from './spacing.js';
import { fontFaceCss } from '../library/resolve.js';
import { vectorWidthCss } from '../theme/logo.js';

export const DEVICE_LAYERS = ['base', 'tablet', 'phone'];

/** Settings a device may override. Content (text, images, links) stays shared. */
export const DEVICE_PARAMS = [
    'marginTop',
    'marginBottom',
    'filter',
    'colorTheme',
    'caption',
    'maxWidth',
    'height',
    'width',
    'align',
    'inheritTextColour',
    'cycleCrossfade',
];

const MEDIA = {
    tablet: '(min-width: 601px) and (max-width: 1024px)',
    phone: '(max-width: 600px)',
};

const ALIGN = { left: 'flex-start', center: 'center', right: 'flex-end' };

const COLOR_RESET = [
    'color:inherit',
    'background-color:transparent',
    'box-shadow:none',
    'clip-path:none',
    '--color-background:unset',
    '--color-text:unset',
    '--color-accent:unset',
    '--color-muted:unset',
    '--block-link-hover-color:unset',
    '--color-placeholder:unset',
];

const CAPTION_RESET = [
    '--font-caption:unset',
    '--caption-squash:unset',
    '--caption-size:unset',
    '--caption-leading:unset',
    '--caption-tracking:unset',
    '--caption-weight:unset',
    '--caption-style:unset',
    '--caption-casing:unset',
    '--caption-color:unset',
];

export function layerOf(layer) {
    return DEVICE_LAYERS.includes(layer) ? layer : 'base';
}

function ownEntry(block, layer) {
    const at = layerOf(layer);
    if (at === 'base') return null;
    const entry = block?.devices?.[at];
    return entry && typeof entry === 'object' ? entry : null;
}

/** The value a device uses: its own, or desktop's when it has none. */
export function paramValue(block, layer, key) {
    const own = ownEntry(block, layer);
    if (own && key in own) return own[key];
    return block?.[key];
}

/** True when tablet or phone stores this setting itself. */
export function paramOverridden(block, layer, key) {
    const own = ownEntry(block, layer);
    return Boolean(own && key in own);
}

function writeDevices(block, layer, entry) {
    const at = layerOf(layer);
    const devices = { ...(block?.devices || {}) };
    if (entry && Object.keys(entry).length) devices[at] = entry;
    else delete devices[at];
    return { devices: Object.keys(devices).length ? devices : undefined };
}

/**
 * Patch for one setting on one device. `undefined` on tablet or phone clears
 * it, so the device uses desktop again. Desktop writes the field itself.
 * @returns {Object}
 */
export function setParam(block, layer, key, value) {
    const at = layerOf(layer);
    if (at === 'base') return { [key]: value };
    const entry = { ...(ownEntry(block, at) || {}) };
    if (value === undefined) delete entry[key];
    else entry[key] = value;
    return writeDevices(block, at, entry);
}

/** Drop every per-device setting on one device, keeping a gallery's columns and gap. */
export function clearParams(block, layer) {
    const at = layerOf(layer);
    if (at === 'base') return {};
    const entry = { ...(ownEntry(block, at) || {}) };
    DEVICE_PARAMS.forEach((key) => delete entry[key]);
    const patch = writeDevices(block, at, entry);
    if (Array.isArray(block?.cells)) {
        patch.cells = block.cells.map((cell) => ({ ...cell, ...clearParams(cell, at) }));
    }
    return patch;
}

/** The block as one device renders it. Desktop comes back unchanged. */
export function blockForLayer(block, layer) {
    const own = ownEntry(block, layer);
    if (!own) return block;
    const next = { ...block };
    DEVICE_PARAMS.forEach((key) => {
        if (key in own) next[key] = own[key];
    });
    return next;
}

function cssName(key) {
    if (key.startsWith('--')) return key;
    if (key === 'WebkitBackdropFilter') return '-webkit-backdrop-filter';
    return key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

function decls(pairs) {
    return pairs.filter((pair) => pair && pair[1] != null && pair[1] !== '').map(([prop, value]) => `${prop}:${value} !important`);
}

function fromStyle(style) {
    return decls(Object.entries(style || {}).map(([key, value]) => [cssName(key), value]));
}

function quoteAttr(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function wrapperFilter(block, filter) {
    const vector = block?.type === 'vector';
    const onGlyphs = (vector || block?.type === 'richtext') && cssBackdropFilter(filter);
    if (vector || onGlyphs) return [];
    const paint = cssFilter(filter);
    const behind = cssBackdropFilter(filter);
    return decls([
        ['filter', paint || 'none'],
        ['backdrop-filter', behind || 'none'],
        ['-webkit-backdrop-filter', behind || 'none'],
    ]);
}

function colorPaint(colorTheme, theme) {
    const style = colorTheme ? colorVars(colorTheme, theme) : {};
    if (!Object.keys(style).length) return decls(COLOR_RESET.map(splitDecl));
    return fromStyle(style);
}

function captionPaint(caption, library) {
    const clean = cleanCaption(caption);
    if (!clean) {
        return {
            decls: decls(CAPTION_RESET.map((line) => {
                const split = line.indexOf(':');
                return [line.slice(0, split), line.slice(split + 1)];
            })),
            face: null,
        };
    }
    const painted = captionVars(clean, library);
    return { decls: fromStyle(painted.vars), face: painted.face };
}

/**
 * Declarations for one device, grouped by selector suffix ('' is the block).
 * Only settings this device stores itself are included.
 */
function layerRules(block, layer, theme, library) {
    const groups = [];
    const add = (suffix, lines) => {
        const list = lines.filter(Boolean);
        if (list.length) groups.push({ suffix, lines: list });
    };
    if (paramOverridden(block, layer, 'marginTop')) {
        add('', decls([['padding-top', cssSpace(paramValue(block, layer, 'marginTop')) || '0px']]));
    }
    if (paramOverridden(block, layer, 'marginBottom')) {
        add('', decls([['padding-bottom', cssSpace(paramValue(block, layer, 'marginBottom')) || '0px']]));
    }
    if (paramOverridden(block, layer, 'filter')) {
        add('', wrapperFilter(block, paramValue(block, layer, 'filter')));
    }
    if (paramOverridden(block, layer, 'colorTheme')) {
        add('', colorPaint(paramValue(block, layer, 'colorTheme'), theme));
    }
    let face = null;
    if (paramOverridden(block, layer, 'caption')) {
        const painted = captionPaint(paramValue(block, layer, 'caption'), library);
        face = painted.face;
        add('', painted.decls);
    }
    if (paramOverridden(block, layer, 'maxWidth')) {
        add(' :is(.html-page, .text-columns-frame)', decls([
            ['max-width', contentWidthCss(paramValue(block, layer, 'maxWidth'))],
        ]));
    }
    if (paramOverridden(block, layer, 'height') && block?.type === 'spacer') {
        const height = Number(paramValue(block, layer, 'height'));
        add(' [data-spacer]', decls([['height', `${Number.isFinite(height) ? height : 4}vh`]]));
    }
    if (paramOverridden(block, layer, 'width') && block?.type === 'vector') {
        add(' .vector-block-graphic', decls([
            ['width', vectorWidthCss(paramValue(block, layer, 'width'))],
        ]));
    }
    if (paramOverridden(block, layer, 'align') && block?.type === 'vector') {
        const align = paramValue(block, layer, 'align') || 'center';
        add(' .vector-block-row', decls([['justify-content', ALIGN[align] || ALIGN.center]]));
    }
    (block?.cells || []).forEach((cell) => {
        if (!cell?.id || !paramOverridden(cell, layer, 'colorTheme')) return;
        const themeValue = paramValue(cell, layer, 'colorTheme');
        const suffix = ` [data-column-id="${quoteAttr(cell.id)}"]`;
        add(suffix, colorPaint(themeValue, theme));
        const fills = columnColors(themeValue, theme).fill;
        add(`${suffix}::before`, decls([['display', fills ? 'block' : 'none']]));
    });
    return { groups, face };
}

function emit(selector, lines) {
    if (!lines.length) return '';
    return `${selector}{${lines.join(';')}}`;
}

function scoped(prefix, root, groups) {
    return groups.map((group) => `${prefix}${emit(`${root}${group.suffix}`, group.lines)}`).join('');
}

/**
 * CSS that applies tablet and phone overrides. Empty when the block follows
 * desktop everywhere. `html[data-header-bp]` wins over the width query so the
 * editor can show desktop inside a narrow frame.
 * @returns {string}
 */
export function deviceCss(block, library, theme) {
    if (!block?.id) return '';
    const root = `[data-page-id="${quoteAttr(block.id)}"]`;
    const faces = [];
    const media = [];
    const forced = [];
    const suffixes = [];
    ['tablet', 'phone'].forEach((layer) => {
        const { groups, face } = layerRules(block, layer, theme, library);
        if (face) faces.push(face);
        if (!groups.length) return;
        groups.forEach((group) => {
            if (!suffixes.includes(group.suffix)) suffixes.push(group.suffix);
        });
        media.push(`@media ${MEDIA[layer]}{${scoped('', root, groups)}}`);
        forced.push(scoped(`html[data-header-bp="${layer}"] `, root, groups));
    });
    if (!media.length) return '';
    const paint = (prefix) => suffixes.map((suffix) => {
        const lines = desktopDecls(block, suffix, theme, library);
        return lines.length ? `${prefix}${emit(`${root}${suffix}`, lines)}` : '';
    }).join('');
    // Desktop first, then the width queries, then a forced device (which beats a
    // narrow frame), then the matching device (which beats that reset).
    const facesCss = faces.length ? fontFaceCss(faces) : '';
    return `${facesCss}${paint('')}${media.join('')}${paint('html[data-header-bp] ')}${forced.join('')}`;
}

function desktopDecls(block, suffix, theme, library) {
    if (suffix === '') {
        const lines = [];
        if (paramOverridden(block, 'tablet', 'marginTop') || paramOverridden(block, 'phone', 'marginTop')) {
            lines.push(...decls([['padding-top', cssSpace(block?.marginTop) || '0px']]));
        }
        if (paramOverridden(block, 'tablet', 'marginBottom') || paramOverridden(block, 'phone', 'marginBottom')) {
            lines.push(...decls([['padding-bottom', cssSpace(block?.marginBottom) || '0px']]));
        }
        if (paramOverridden(block, 'tablet', 'filter') || paramOverridden(block, 'phone', 'filter')) {
            lines.push(...wrapperFilter(block, block?.filter));
        }
        if (paramOverridden(block, 'tablet', 'colorTheme') || paramOverridden(block, 'phone', 'colorTheme')) {
            lines.push(...(block?.colorTheme ? fromStyle(colorVars(block.colorTheme, theme)) : decls(COLOR_RESET.map(splitDecl))));
        }
        if (paramOverridden(block, 'tablet', 'caption') || paramOverridden(block, 'phone', 'caption')) {
            lines.push(...captionPaint(block?.caption, library).decls);
        }
        return lines;
    }
    if (suffix === ' :is(.html-page, .text-columns-frame)') {
        return decls([['max-width', contentWidthCss(block?.maxWidth)]]);
    }
    if (suffix === ' [data-spacer]') {
        const height = Number(block?.height);
        return decls([['height', `${Number.isFinite(height) ? height : 4}vh`]]);
    }
    if (suffix === ' .vector-block-graphic') {
        return decls([['width', vectorWidthCss(block?.width)]]);
    }
    if (suffix === ' .vector-block-row') {
        return decls([['justify-content', ALIGN[block?.align] || ALIGN.center]]);
    }
    const cellMatch = suffix.match(/^ \[data-column-id="(.*)"\](?:::before)?$/);
    if (cellMatch) {
        const cell = (block?.cells || []).find((entry) => entry?.id === cellMatch[1]);
        if (!cell) return [];
        if (suffix.endsWith('::before')) {
            return decls([['display', columnColors(cell.colorTheme, theme).fill ? 'block' : 'none']]);
        }
        return cell.colorTheme ? fromStyle(colorVars(cell.colorTheme, theme)) : decls(COLOR_RESET.map(splitDecl));
    }
    return [];
}

function splitDecl(line) {
    const split = line.indexOf(':');
    return [line.slice(0, split), line.slice(split + 1)];
}

export default {
    DEVICE_LAYERS,
    DEVICE_PARAMS,
    layerOf,
    paramValue,
    paramOverridden,
    setParam,
    clearParams,
    blockForLayer,
    deviceCss,
};
